import type { ReactNode, CSSProperties, JSX } from 'react';
import { useIntl, type MessageDescriptor } from '@doxyme/locale';
import { Button, ButtonBar } from '@doxyme/ui/components/foundations';
import { BodyS } from '@doxyme/ui/typography';
import { useUpsellCardStyles } from './upsellCard.styles';
import Box from '@mui/material/Box';

export type UpsellCardProps = {
  image: ReactNode;
  imageSize?: CSSProperties['width'];
  content: {
    icon: ReactNode;
    title: MessageDescriptor['id'];
    description: MessageDescriptor['id'];
  }[];
  onCancel?: () => void;
  onRedirect?: () => void;
};

export const UpsellCard = ({
  image,
  imageSize,
  content,
  onCancel,
  onRedirect,
}: UpsellCardProps): JSX.Element => {
  const { formatMessage } = useIntl();
  const styles = useUpsellCardStyles(imageSize);

  const isButtonBarVisible =
    typeof onCancel === 'function' && typeof onRedirect === 'function';

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: styles.wrapperCSS.columnGap,
      }}
    >
      <Box
        css={styles.cardCSS}
        sx={{
          width: '100%',
          height: '100%',
        }}
      >
        <Box
          css={styles.imageWrapperCSS}
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {image}
        </Box>

        <Box
          css={styles.contentCSS}
          sx={{
            display: 'flex',
            width: '100%',
            height: '100%',
            alignItems: 'center',
            justifyContent: 'flex-start',
          }}
        >
          {content.map(({ icon, title, description }) => (
            <Box
              key={title}
              css={styles.contentItemCSS}
              sx={{
                display: 'flex',
                width: '100%',
                height: '100%',
                alignItems: 'center',
                justifyContent: 'flex-start',
              }}
            >
              <Box css={styles.iconWrapperCSS}>{icon}</Box>

              <Box>
                <BodyS css={styles.titleCSS}>
                  {formatMessage({ id: title })}
                </BodyS>
                <BodyS css={styles.descriptionCSS}>
                  {formatMessage({ id: description })}
                </BodyS>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
      {isButtonBarVisible && (
        <ButtonBar spacing={4} align='flex-end'>
          <Button type='outline' onClick={onCancel} css={styles.buttonCSS}>
            {formatMessage({ id: 'upsellCard.action.cancel' })}
          </Button>
          <Button type='upgrade' onClick={onRedirect} css={styles.buttonCSS}>
            {formatMessage({ id: 'upsellCard.action.upgrade' })}
          </Button>
        </ButtonBar>
      )}
    </Box>
  );
};
